import { Button, SelectOptionType } from '@octano/global-ui';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Col, Row } from 'reactstrap';

import RemoteSearchSelect from '../../../components/form/RemoteSearchSelect';
import { useValidations } from '../../../hooks/useValidations';
import { UpdateTeacherOrAssistantRow } from '../types';
import { CreateSectionFormType } from './CreateSectionForm';
import { SectionValuesForm } from './SectionForm';
import TeacherRowActions from './TeacherRowActions';

type ExtraFieldsProps = {
  title: 'teachers' | 'assistants';
  inputLabel: string;
  search: (searchText: string) => Promise<SelectOptionType[]>;
  buttonLabel: string;
  supportsOnline?: boolean;
  supportsPresential?: boolean;
};

const ExtraFields: FunctionComponent<ExtraFieldsProps> = ({
  title,
  inputLabel,
  search,
  buttonLabel,
  children,
  supportsOnline = false,
  supportsPresential = false,
}) => {
  const { msgValidations } = useValidations();
  const { control, setValue, getValues } = useFormContext<
    | SectionValuesForm
    | CreateSectionFormType
    | {
        teachers: UpdateTeacherOrAssistantRow[];
        assistants: UpdateTeacherOrAssistantRow[];
      }
  >();
  const { fields = [], remove, append } = useFieldArray({
    control,
    name: title,
  });

  const lastField = useMemo(
    () => (fields.length ? fields[fields.length - 1] : null),
    [fields],
  );

  const handleReplace = useCallback(
    (i: number, row: UpdateTeacherOrAssistantRow) => {
      const items = [...getValues(title)];
      items[i] = row;
      setValue(title, items);
    },
    [setValue, getValues, title],
  );

  const handleRemoveItem = useCallback(
    (i: number) => {
      const items = [...getValues(title)];
      if (items.length === 1) {
        handleReplace(i, {
          label: '',
          value: 0,
          isOnline: false,
          isPresential: false,
          order: i,
        });
      } else {
        remove(i);
      }
    },
    [remove, getValues, handleReplace, title],
  );

  const handleAdd = useCallback(() => {
    append({
      order: 0,
      label: '',
      value: 0,
      isOnline: false,
      isPresential: false,
    });
  }, [append]);

  return (
    <>
      {fields.map((elem, index) => (
        <TeacherRowActions
          key={String(index)}
          isOnline={!!elem.isOnline}
          isPresential={!!elem.isPresential}
          onPressDelete={() => handleRemoveItem(index)}
          onPressOnline={
            supportsOnline && elem.value
              ? () =>
                  handleReplace(index, {
                    order: index,
                    value: elem.value,
                    label: elem.label,
                    isPresential: !!elem.isPresential,
                    isOnline: !elem.isOnline,
                  })
              : undefined
          }
          onPressPresential={
            supportsPresential && elem.value
              ? () =>
                  handleReplace(index, {
                    order: index,
                    value: elem.value,
                    label: elem.label,
                    isPresential: !elem.isPresential,
                    isOnline: !!elem.isOnline,
                  })
              : undefined
          }
        >
          <RemoteSearchSelect
            name={`${title}-${index}-${elem.id ?? 0}`}
            label={inputLabel}
            filterOptions={search}
            rules={{ required: msgValidations.required }}
            isClearable={false}
            onChange={(next) =>
              handleReplace(index, {
                order: index,
                value: +next?.value,
                label: next?.label || '',
                isPresential: false,
                isOnline: false,
              })
            }
            value={elem}
          />
        </TeacherRowActions>
      ))}
      {!!(supportsOnline || supportsPresential) && (
        <Row style={{ paddingRight: 50 }}>
          <Col md={6}>{children}</Col>
          <Col md={6} className="mb-4">
            <Button
              type="button"
              outlined
              size="sm"
              onClick={handleAdd}
              text={buttonLabel}
              disabled={!lastField?.value}
              fullwidth
              icon="plus"
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default ExtraFields;
